/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, SetStateAction } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';
import Header from '../components/header';
import Tabs from '../components/tabs';
import MenuModal from '../components/modals/menu-modal';
import {
  useGetGroupQuery,
  useUpdateGroupMutation,
  useChangePermissionsMutation,
} from '../services/groups.service';
import TABS from './__fixtures__/group-tabs';
import { useApp } from '../hooks/use-app';
import { ChangePermission, Resource } from '../models/group';
import GroupMenuList from './group-menu-list';
import {
  getHasNothingAvailable, getHasSelectedAllExisting,
  getPage,
  getSearchString,
  getSelectedItems, getTotalExistingItems,
} from './menu-list.selectors';
import { useAppSelector, useAppDispatch } from '../hooks';
import { reset, nextPage, prevPage } from './menu-list.slice';

export interface Props {
  id: string;
  resource: Resource;
  setGroup: React.Dispatch<SetStateAction<{ id: string; resource: Resource }>>;
}

function getPath() {
  return window.location.pathname.split('/').pop() as Resource;
}

const RESOURCES: Resource[] = Object.values(Resource);

function getResourceIDString(resource: Resource) {
  if (resource === Resource.Apps) return 'formIds';
  return `${resource.slice(0, -1)}Ids`;
}

export default function GroupsDetails({ id: groupId, resource, setGroup }: Props) {
  const dispatch = useAppDispatch();
  const [resourceType, setResourceType] = useState(resource);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [isBackButton, setIsBackButton] = useState(false);
  const { data: group, isLoading, error } = useGetGroupQuery({ id: groupId });
  const [updateGroup, { error: groupUpdateError }] = useUpdateGroupMutation();
  const [changePermission] = useChangePermissionsMutation();
  const app = useApp();
  const groupTabs = TABS(groupId);
  const hasGroup = !!group && !error;

  const selectedResources = useAppSelector(getSelectedItems);
  const hasNoResourceAvailable = useAppSelector(getHasNothingAvailable);
  const page = useAppSelector(getPage);

  React.useEffect(() => {
    window.addEventListener('popstate', handleBackEvent);
    return () => window.removeEventListener('popstate', handleBackEvent);
  }, []);

  React.useEffect(() => triggerHistoryChange(resourceType), [resourceType]);
  function triggerHistoryChange(source: Resource) {
    const url = new URL(`/groups/${groupId}/${source}`, window.location.origin);

    if (isBackButton) {
      window.history.replaceState({}, '', url);
      setIsBackButton(false);
    } else {
      window.history.pushState({}, '', url);
    }
  }

  const handleBackEvent = () => {
    if (window.location.pathname === '/groups') {
      const url = new URL('/groups', window.location.origin);
      window.history.replaceState({}, '', url);
      setGroup({ id: '', resource: Resource.Members });
    } else {
      unstable_batchedUpdates(() => {
        setIsBackButton(true);
        setResourceType(getPath());
      });
    }
  };

  const handleModalClose = () => {
    setAnchor(null);
    setTimeout(() => dispatch(reset()), 400); // wait for animation to finish
  };
  const handleTabChange = (idx: number) => setResourceType(RESOURCES[idx]);
  const handleOpenAddResource = (ele: EventTarget & HTMLButtonElement) => setAnchor(ele);
  const addAll = useAppSelector(getHasSelectedAllExisting);
  const totalElementCount = useAppSelector(getTotalExistingItems);
  const search = useAppSelector(getSearchString);
  const handleSaveResource = () => {
    const selectedResourcesIds = selectedResources.map((r) => r.id);
    const selectedResourcesCount = selectedResourcesIds.length;
    if (selectedResourcesCount) {
      changePermission({
        search,
        type: ChangePermission[resourceType],
        add: selectedResourcesIds,
        groupId,
        resource: resourceType,
        count: addAll ? totalElementCount : selectedResourcesCount,
        item: selectedResources[0].primaryText,
        addAll,
      });
    }

    setAnchor(null);
    dispatch(reset());
  };

  const alreadyAddedResources = group
    ? group[getResourceIDString(resourceType)]
    : [];

  const handleNextPage = () => {
    dispatch(nextPage());
  };

  const handlePrevPage = () => {
    dispatch(prevPage());
  };

  const renderFooter = () => {
    const from = (page - 1) * 1000 + 1;
    const to = page * 1000;
    const total = totalElementCount;
    const isLastPage = to >= total;
    const isFirstPage = page === 1;

    return (
      <Box paddingX={2} fontSize={12}>
        <Box display="flex" justifyContent="flex-start" alignItems="center">
          <span>
            {`${from} - ${isLastPage ? total : to} of ${total}`}
          </span>
          <Box marginLeft={2}>
            <IconButton onClick={handlePrevPage} disabled={isFirstPage}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={handleNextPage} disabled={isLastPage}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Stack height="100%" direction="column" spacing={3}>
        {isLoading && (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {!!error && 'There was an error fetching the group.'}
        {hasGroup && (
          <>
            <Header
              title={group.name}
              subtitle={group.description}
              subtitleEmptyText="ADD A DESCRIPTION"
              isEditable={app.role.canManageGroups && !group.isManaged}
              // @ts-ignore
              errors={groupUpdateError?.data?.errors}
              requiredTitleText="A group name is required"
              onSave={{
                method: updateGroup,
                params: { id: groupId },
              }}
            />
            {/* NOTE  height is set for rendering the virtual table */}
            <Box sx={{ height: '100%' }}>
              <Tabs
                tabs={groupTabs}
                onActionButton={handleOpenAddResource}
                tabIndex={RESOURCES.indexOf(resourceType)}
                onTabChange={handleTabChange}
              />
              <MenuModal
                anchorEl={anchor}
                negative={hasNoResourceAvailable ? 'Close' : 'Cancel'}
                positive={hasNoResourceAvailable ? undefined : 'Add'}
                onClose={handleModalClose}
                onNegative={handleModalClose}
                onPositive={handleSaveResource}
                title=""
                noPadding
                renderFooter={hasNoResourceAvailable ? undefined : renderFooter}
              >
                <GroupMenuList
                  id={groupId}
                  resource={resourceType}
                  alreadyAdded={alreadyAddedResources}
                />
              </MenuModal>
            </Box>
          </>
        )}
      </Stack>
    </>
  );
}
